import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

type NavigationState = {
    histories: Record<string, string[]>;
};

type NavigationActions = {
    getHistory: (entryId: string) => string[];
    resetHistory: (entryId: string) => void;
    pushPage: (entryId: string, pageId: string) => void;
    popPage: (entryId: string) => string | null;
    setHistory: (entryId: string, history: string[]) => void;
};

const initialState: NavigationState = {
    histories: {},
};

export const useFormNavigationStore = create<NavigationState & NavigationActions>()(
    persist(
        (set, get) => ({
            ...initialState,
            getHistory: (entryId) => get().histories[entryId] ?? [],
            resetHistory: (entryId) =>
                set((state) => ({
                    histories: {
                        ...state.histories,
                        [entryId]: [],
                    },
                })),
            pushPage: (entryId, pageId) =>
                set((state) => {
                    const current = state.histories[entryId] ?? [];
                    if (current[current.length - 1] === pageId) {
                        return state;
                    }
                    return {
                        histories: {
                            ...state.histories,
                            [entryId]: [...current, pageId],
                        },
                    };
                }),
            popPage: (entryId) => {
                const current = get().histories[entryId] ?? [];
                if (current.length <= 1) return null;
                const nextHistory = current.slice(0, -1);
                set((state) => ({
                    histories: {
                        ...state.histories,
                        [entryId]: nextHistory,
                    },
                }));
                return nextHistory[nextHistory.length - 1] ?? null;
            },
            setHistory: (entryId, history) =>
                set((state) => ({
                    histories: {
                        ...state.histories,
                        [entryId]: history,
                    },
                })),
        }),
        {
            name: "form-navigation-store",
            storage: createJSONStorage(() => sessionStorage),
        }
    )
);

export function formNavigationStorageKey(entryId: string): string {
    return `form-nav:${entryId}`;
}
